<template>
  <section class="part-maintenance-card">
    <header class="part-maintenance-head">
      <div>
        <span class="part-maintenance-kicker">Part-scoped operations</span>
        <h2>Maintenance</h2>
        <p>Run backend-provided repair and health actions for this catalog record.</p>
      </div>
      <div class="part-maintenance-snapshot">
        <span><small>Price</small><strong>{{ priceLabel }}</strong></span>
        <span><small>Availability</small><strong :class="availabilityTone">{{ availabilityLabel }}</strong></span>
      </div>
    </header>

    <div v-if="actions.length" class="part-maintenance-grid">
      <article
        v-for="action in actions"
        :key="action.key"
        class="part-maintenance-action"
        :class="'state-' + actionTone(action.key)"
      >
        <div class="part-maintenance-action-copy">
          <div class="part-maintenance-action-title">
            <strong>{{ action.label || prettyLabel(action.key) }}</strong>
            <span v-if="action.jobKey">{{ action.jobKey }}</span>
          </div>
          <p>{{ action.description || 'Run this maintenance action for the current part.' }}</p>
        </div>

        <label v-if="action.key === 'auto-fix'" class="part-maintenance-dry-run">
          <input
            type="checkbox"
            :checked="dryRunFor(action.key)"
            :disabled="actionBusy(action.key)"
            @change="setDryRun(action.key, $event.target.checked)"
          >
          <span>
            <strong>Preview only</strong>
            <small>Show deterministic changes without applying them.</small>
          </span>
        </label>

        <div v-if="stateFor(action.key).run || stateFor(action.key).message || stateFor(action.key).error" class="part-maintenance-run">
          <div class="part-maintenance-run-line">
            <span class="part-maintenance-run-status" :class="'tone-' + actionTone(action.key)">
              {{ runStatusLabel(stateFor(action.key).run, stateFor(action.key)) }}
            </span>
            <span v-if="runId(stateFor(action.key).run)">Run #{{ runId(stateFor(action.key).run) }}</span>
          </div>
          <div v-if="runProgress(stateFor(action.key).run) !== null" class="part-maintenance-progress" aria-hidden="true">
            <span :style="{ width: runProgress(stateFor(action.key).run) + '%' }"></span>
          </div>
          <p v-if="stateFor(action.key).message" class="part-maintenance-message">{{ stateFor(action.key).message }}</p>
          <p v-if="stateFor(action.key).error" class="part-maintenance-error">{{ stateFor(action.key).error }}</p>

          <details v-if="stateFor(action.key).error || runLogs(stateFor(action.key).run).length" class="part-maintenance-details">
            <summary>
              Job details
              <span v-if="runLogs(stateFor(action.key).run).length">{{ runLogs(stateFor(action.key).run).length }} log entries</span>
            </summary>
            <div>
              <pre v-if="stateFor(action.key).error">{{ stateFor(action.key).error }}</pre>
              <pre v-for="(line, index) in runLogs(stateFor(action.key).run)" :key="index">{{ line }}</pre>
            </div>
          </details>
        </div>

        <button
          type="button"
          class="part-maintenance-run-button"
          :disabled="actionBusy(action.key) || !partId || !partType"
          @click="runAction(action)"
        >
          {{ actionButtonLabel(action) }}
        </button>
      </article>
    </div>
    <div v-else class="part-maintenance-empty">
      No maintenance actions are available for this part.
    </div>
  </section>
</template>

<script>
import axios from 'axios'
import { apiDomain } from '../../../config'
import realtime from '../../../services/realtime'

export default {
  name: 'PartMaintenancePanel',
  props: {
    actions: { type: Array, default: function () { return [] } },
    part: { type: Object, default: null },
    partType: { type: String, default: '' },
    partId: { type: [String, Number], default: '' }
  },
  data () {
    return {
      actionStates: {},
      dryRunModes: {},
      pollTimers: {},
      trackingCleanups: {}
    }
  },
  computed: {
    priceLabel () {
      const part = this.part || {}
      const general = part.general || {}
      const price = part.price && typeof part.price === 'object' ? part.price : {}
      const value = price.price || price.current || price.amount || price.value ||
        part.amzn_price || part.amazonPrice || part.currentPrice || general.amzn_price || general.amazonPrice
      if (value === null || value === undefined || value === '') return 'Not available'
      if (typeof value === 'number') return `$${value.toFixed(2)}`
      return String(value)
    },
    availabilityLabel () {
      const part = this.part || {}
      const general = part.general || {}
      const raw = part.availabilityStatus || part.availability_status || general.availabilityStatus ||
        general.availability_status || part.available || part.is_available || general.is_available
      if (raw === true || raw === 1 || raw === '1') return 'Available'
      if (raw === false || raw === 0 || raw === '0') return 'Unavailable'
      const text = String(raw || '').trim()
      return text ? this.prettyLabel(text) : 'Unknown'
    },
    availabilityTone () {
      const value = this.availabilityLabel.toLowerCase()
      if (value === 'available') return 'is-good'
      if (value === 'unavailable' || value.indexOf('out of stock') >= 0) return 'is-bad'
      return ''
    }
  },
  watch: {
    actions: {
      immediate: true,
      handler (actions) {
        const availableActions = actions || []
        availableActions.forEach((action) => {
          if (!action || !action.key) return
          if (!this.actionStates[action.key]) this.$set(this.actionStates, action.key, this.emptyState())
          if (action.key === 'auto-fix' && this.dryRunModes[action.key] === undefined) {
            this.$set(this.dryRunModes, action.key, true)
          }
        })
      }
    }
  },
  beforeDestroy () {
    Object.keys(this.pollTimers).forEach((key) => this.clearPoll(key))
    Object.keys(this.trackingCleanups).forEach((key) => this.cleanupTracking(key))
  },
  methods: {
    authHeaders () {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.usertoken}`
      }
    },
    emptyState () {
      return {
        starting: false,
        finalizing: false,
        run: null,
        message: '',
        error: ''
      }
    },
    stateFor (key) {
      if (!this.actionStates[key]) this.$set(this.actionStates, key, this.emptyState())
      return this.actionStates[key]
    },
    updateState (key, patch) {
      this.$set(this.actionStates, key, Object.assign({}, this.stateFor(key), patch || {}))
    },
    dryRunFor (key) {
      return this.dryRunModes[key] !== false
    },
    setDryRun (key, value) {
      this.$set(this.dryRunModes, key, !!value)
    },
    actionBusy (key) {
      const state = this.stateFor(key)
      return !!(state.starting || state.finalizing || this.isRunActive(state.run))
    },
    async runAction (action) {
      if (!action || !action.key || this.actionBusy(action.key)) return
      if (action.key === 'refresh-images') {
        const confirmed = window.confirm(
          'Re-scrape images for this part?\n\nThis replaces the current server-side image set. Manual image edits that have not been saved may be lost.'
        )
        if (!confirmed) return
      }
      const dryRun = action.key === 'auto-fix' ? this.dryRunFor(action.key) : null
      if (action.key === 'auto-fix' && dryRun === false) {
        const confirmed = window.confirm(
          'Apply deterministic fixes to this part?\n\nThis writes the previewed fixes to the catalog record.'
        )
        if (!confirmed) return
      }

      this.cleanupTracking(action.key)
      this.updateState(action.key, {
        starting: true,
        finalizing: false,
        run: null,
        message: '',
        error: ''
      })

      try {
        const body = action.key === 'auto-fix' ? { dryRun } : {}
        const { data } = await axios.post(
          `${apiDomain}catalog-parts/${encodeURIComponent(this.partType)}/${encodeURIComponent(this.partId)}/maintenance/${encodeURIComponent(action.key)}`,
          body,
          { headers: this.authHeaders() }
        )
        const run = this.extractRun(data)
        const started = data && data.started !== undefined ? !!data.started : !!run
        const reason = String((data && data.reason) || '').toLowerCase()

        if (!started && reason !== 'already-running') {
          this.updateState(action.key, {
            starting: false,
            run,
            error: (data && (data.message || data.reason)) || 'The maintenance action did not start.'
          })
          return
        }

        this.updateState(action.key, {
          starting: false,
          run,
          message: reason === 'already-running'
            ? 'This action is already running. Showing the active run.'
            : 'Maintenance job started.'
        })

        if (!run || !this.runId(run)) {
          this.updateState(action.key, {
            error: reason === 'already-running'
              ? 'The action is already running, but no active run details were returned.'
              : 'The action started, but no job run was returned for tracking.'
          })
          return
        }

        if (this.isTerminalRun(run)) this.finishRun(action.key, run)
        else this.trackRun(action.key, run)
      } catch (error) {
        this.updateState(action.key, {
          starting: false,
          error: this.errorMessage(error)
        })
      }
    },
    trackRun (key, run) {
      this.updateState(key, { run })
      const id = this.runId(run)
      if (!id) return
      const topic = `jobs:${id}`
      const cleanups = []
      cleanups.push(realtime.subscribe(topic, {
        'job:snapshot': (payload) => this.handleRunEvent(key, payload),
        'job:progress': (payload) => this.handleRunEvent(key, payload),
        'job:log': (payload) => this.handleRunEvent(key, payload),
        'job:finished': (payload) => this.handleRunEvent(key, payload),
        'job:error': (payload) => this.handleRunError(key, payload)
      }))
      cleanups.push(realtime.onStateChange((state, connected, detail) => {
        if (connected && state === 'open') {
          this.clearPoll(key)
          this.updateState(key, { error: '' })
          realtime.refresh(topic)
        } else if (!connected) {
          if (state === 'error') this.updateState(key, { error: realtime.errorMessage(detail) })
          this.schedulePoll(key, 500)
        }
      }))
      this.$set(this.trackingCleanups, key, cleanups)

      if (realtime.isConnected()) realtime.refresh(topic)
      else this.schedulePoll(key, 500)
    },
    handleRunEvent (key, payload) {
      const state = this.stateFor(key)
      const incoming = this.extractRun(payload) || payload || {}
      const run = Object.assign({}, state.run || {}, incoming)
      const logs = this.runLogs(state.run)
        .concat(this.runLogs(payload), this.runLogs(incoming))
        .filter((line, index, values) => values.indexOf(line) === index)
        .slice(-40)
      if (logs.length) run.logs = logs
      if (!run.id && !run.runId && !run.run_id) {
        run.id = this.runId(state.run)
      }
      this.updateState(key, { run })
      if (this.isTerminalRun(run)) this.finishRun(key, run)
    },
    handleRunError (key, payload) {
      const data = payload || {}
      const run = this.extractRun(data)
      if (run) this.handleRunEvent(key, data)
      else this.updateState(key, { error: data.message || data.reason || realtime.errorMessage(data) })
    },
    schedulePoll (key, delay) {
      this.clearPoll(key)
      const timer = window.setTimeout(() => this.pollRun(key), delay || 2500)
      this.$set(this.pollTimers, key, timer)
    },
    async pollRun (key) {
      this.clearPoll(key)
      const state = this.stateFor(key)
      const id = this.runId(state.run)
      if (!id || this.isTerminalRun(state.run)) return
      try {
        const { data } = await axios.get(
          `${apiDomain}jobs/runs/${encodeURIComponent(id)}`,
          { headers: this.authHeaders() }
        )
        const run = Object.assign({}, state.run || {}, this.extractRun(data) || data || {})
        this.updateState(key, { run, error: '' })
        if (this.isTerminalRun(run)) this.finishRun(key, run)
        else this.schedulePoll(key, 2500)
      } catch (error) {
        this.updateState(key, { error: `Could not refresh job status: ${this.errorMessage(error)}` })
        this.schedulePoll(key, 3500)
      }
    },
    async finishRun (key, run) {
      const state = this.stateFor(key)
      if (state.finalizing) return
      this.cleanupTracking(key)
      const succeeded = this.runSucceeded(run)
      this.updateState(key, {
        finalizing: succeeded,
        run,
        message: succeeded ? 'Completed. Refreshing the part record...' : '',
        error: succeeded ? '' : this.runError(run) || `Job ${this.prettyLabel(this.runStatus(run))}.`
      })
      if (!succeeded) return

      this.$emit('completed', {
        action: key,
        run,
        complete: (error) => {
          this.updateState(key, {
            finalizing: false,
            message: error ? 'The job completed, but the part details could not be refreshed.' : 'Completed. Part details are up to date.',
            error: error ? this.errorMessage(error) : ''
          })
        }
      })
    },
    cleanupTracking (key) {
      this.clearPoll(key)
      const cleanups = this.trackingCleanups[key] || []
      cleanups.forEach((cleanup) => {
        if (typeof cleanup === 'function') cleanup()
      })
      this.$delete(this.trackingCleanups, key)
    },
    clearPoll (key) {
      const timer = this.pollTimers[key]
      if (timer) window.clearTimeout(timer)
      this.$delete(this.pollTimers, key)
    },
    extractRun (data) {
      if (!data) return null
      if (data.run) return data.run
      if (data.jobRun) return data.jobRun
      if (data.job_run) return data.job_run
      if (data.activeRun) return data.activeRun
      if (data.active_run) return data.active_run
      if (data.data) return this.extractRun(data.data)
      if (data.id || data.runId || data.run_id) return data
      return null
    },
    runId (run) {
      return run && (run.id || run.runId || run.run_id)
    },
    runStatus (run) {
      return String((run && (run.status || run.state)) || 'queued').toLowerCase()
    },
    isRunActive (run) {
      return !!run && ['starting', 'queued', 'pending', 'running', 'active'].indexOf(this.runStatus(run)) >= 0
    },
    isTerminalRun (run) {
      return ['success', 'succeeded', 'completed', 'complete', 'failed', 'error', 'cancelled', 'canceled'].indexOf(this.runStatus(run)) >= 0
    },
    runSucceeded (run) {
      return ['success', 'succeeded', 'completed', 'complete'].indexOf(this.runStatus(run)) >= 0
    },
    runProgress (run) {
      if (!run) return null
      const raw = run.percent !== undefined ? run.percent
        : run.progressPercent !== undefined ? run.progressPercent
          : run.progress_percent !== undefined ? run.progress_percent
            : typeof run.progress === 'number' ? run.progress : null
      if (raw === null || raw === undefined || raw === '') return null
      const number = Number(raw)
      if (!isFinite(number)) return null
      return Math.max(0, Math.min(100, number <= 1 ? number * 100 : number))
    },
    runStatusLabel (run, state) {
      if (state && state.starting) return 'Starting'
      if (state && state.finalizing) return 'Refreshing'
      return this.prettyLabel(this.runStatus(run)) || 'Ready'
    },
    actionTone (key) {
      const state = this.stateFor(key)
      if (state.error || (state.run && !this.runSucceeded(state.run) && this.isTerminalRun(state.run))) return 'bad'
      if (state.finalizing || this.isRunActive(state.run)) return 'active'
      if (state.run && this.runSucceeded(state.run)) return 'good'
      return 'idle'
    },
    actionButtonLabel (action) {
      const state = this.stateFor(action.key)
      if (state.starting) return 'Starting...'
      if (state.finalizing) return 'Refreshing...'
      if (this.isRunActive(state.run)) {
        const progress = this.runProgress(state.run)
        return progress === null ? 'Running...' : `Running ${Math.round(progress)}%`
      }
      if (action.key === 'auto-fix') return this.dryRunFor(action.key) ? 'Preview fixes' : 'Apply fixes'
      return action.label || this.prettyLabel(action.key)
    },
    runLogs (run) {
      if (!run) return []
      const raw = run.logs || run.log || run.logAppend || run.log_append || run.messages || run.events || []
      const values = Array.isArray(raw) ? raw : [raw]
      return values
        .map((entry) => {
          if (entry === null || entry === undefined) return ''
          if (typeof entry === 'string') return entry
          return entry.message || entry.text || entry.log || entry.detail || JSON.stringify(entry)
        })
        .reduce((lines, entry) => lines.concat(String(entry || '').split(/\r?\n/)), [])
        .filter((entry) => entry.trim())
        .slice(-40)
    },
    runError (run) {
      if (!run) return ''
      const stats = run.stats || run.result || run.output || {}
      return run.error || run.lastError || run.last_error || stats.error || stats.lastError || stats.message || ''
    },
    prettyLabel (value) {
      return String(value || '')
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (character) => character.toUpperCase())
    },
    errorMessage (error) {
      return (error && error.response && error.response.data && (error.response.data.message || error.response.data.error)) ||
        (error && error.message) || 'Request failed.'
    }
  }
}
</script>

<style scoped>
.part-maintenance-card {
  margin: 12px 0 0;
  padding: 15px;
  border: 1px solid rgba(25, 151, 198, 0.42);
  border-radius: 8px;
  background:
    radial-gradient(circle at 4% 0%, rgba(25, 151, 198, 0.1), transparent 28%),
    linear-gradient(145deg, rgba(14, 21, 32, 0.88), rgba(6, 10, 17, 0.76));
  color: #cfd2da;
}

.part-maintenance-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 13px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.54);
}

.part-maintenance-head > div:first-child {
  display: grid;
  gap: 3px;
}

.part-maintenance-kicker {
  color: #20bff3;
  font-size: 8pt;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.part-maintenance-head h2 {
  margin: 0;
  color: #e6ebf2;
  font-size: 12pt;
  font-weight: 700;
}

.part-maintenance-head p,
.part-maintenance-action p {
  margin: 0;
  color: #8490a3;
  font-size: 8.5pt;
  line-height: 1.42;
}

.part-maintenance-snapshot {
  display: grid;
  grid-template-columns: repeat(2, minmax(100px, auto));
  gap: 7px;
}

.part-maintenance-snapshot > span {
  display: grid;
  gap: 2px;
  min-width: 108px;
  padding: 7px 9px;
  border: 1px solid rgba(67, 72, 87, 0.65);
  border-radius: 6px;
  background: rgba(4, 7, 12, 0.38);
}

.part-maintenance-snapshot small {
  color: #6f7a8e;
  font-size: 7pt;
  font-weight: 900;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.part-maintenance-snapshot strong {
  overflow: hidden;
  color: #dfe5ef;
  font-size: 8.5pt;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.part-maintenance-snapshot strong.is-good { color: #8fe8c4; }
.part-maintenance-snapshot strong.is-bad { color: #f49aaa; }

.part-maintenance-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
}

.part-maintenance-action {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px 12px;
  align-items: start;
  min-width: 0;
  padding: 11px;
  border: 1px solid rgba(67, 72, 87, 0.7);
  border-radius: 7px;
  background: rgba(4, 7, 12, 0.34);
  transition: border-color 0.15s ease, background 0.15s ease;
}

.part-maintenance-action.state-active {
  border-color: rgba(25, 151, 198, 0.58);
  background: rgba(25, 151, 198, 0.07);
}

.part-maintenance-action.state-good { border-color: rgba(27, 201, 142, 0.42); }
.part-maintenance-action.state-bad { border-color: rgba(232, 79, 106, 0.44); }

.part-maintenance-action-copy {
  display: grid;
  gap: 5px;
  min-width: 0;
}

.part-maintenance-action-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
}

.part-maintenance-action-title strong {
  color: #dfe5ef;
  font-size: 9.2pt;
}

.part-maintenance-action-title span {
  padding: 2px 6px;
  border: 1px solid rgba(67, 72, 87, 0.6);
  border-radius: 999px;
  color: #748095;
  font-size: 6.8pt;
  font-weight: 900;
}

.part-maintenance-run-button {
  align-self: center;
  min-width: 106px;
  min-height: 32px;
  padding: 0 10px;
  border: 1px solid rgba(25, 151, 198, 0.58);
  border-radius: 6px;
  background: rgba(25, 151, 198, 0.1);
  color: #9bdff5;
  cursor: pointer;
  font-family: inherit;
  font-size: 7.8pt;
  font-weight: 900;
  text-transform: uppercase;
}

.part-maintenance-run-button:hover:not(:disabled) {
  border-color: rgba(32, 191, 243, 0.82);
  background: rgba(25, 151, 198, 0.18);
  color: #e5f8ff;
}

.part-maintenance-run-button:disabled {
  cursor: wait;
  opacity: 0.56;
}

.part-maintenance-dry-run,
.part-maintenance-run {
  grid-column: 1 / -1;
}

.part-maintenance-dry-run {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 9px;
  border: 1px solid rgba(224, 192, 126, 0.32);
  border-radius: 6px;
  background: rgba(77, 58, 31, 0.1);
  cursor: pointer;
}

.part-maintenance-dry-run > span {
  display: grid;
  gap: 1px;
}

.part-maintenance-dry-run strong {
  color: #e0c07e;
  font-size: 8pt;
}

.part-maintenance-dry-run small {
  color: #8f826d;
  font-size: 7.4pt;
}

.part-maintenance-run {
  display: grid;
  gap: 6px;
  padding-top: 8px;
  border-top: 1px solid rgba(67, 72, 87, 0.44);
}

.part-maintenance-run-line {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #748095;
  font-size: 7.4pt;
}

.part-maintenance-run-status {
  padding: 2px 7px;
  border: 1px solid rgba(67, 72, 87, 0.66);
  border-radius: 999px;
  color: #aeb6c8;
  font-weight: 900;
  text-transform: uppercase;
}

.part-maintenance-run-status.tone-active { border-color: rgba(25, 151, 198, 0.5); color: #9bdff5; }
.part-maintenance-run-status.tone-good { border-color: rgba(27, 201, 142, 0.46); color: #8fe8c4; }
.part-maintenance-run-status.tone-bad { border-color: rgba(232, 79, 106, 0.46); color: #f49aaa; }

.part-maintenance-progress {
  height: 4px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(67, 72, 87, 0.42);
}

.part-maintenance-progress span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #16b4ed;
  transition: width 0.2s ease;
}

.part-maintenance-message,
.part-maintenance-error {
  margin: 0;
  font-size: 7.8pt;
}

.part-maintenance-message { color: #9bb0c4; }
.part-maintenance-error { color: #f3a3b1; }

.part-maintenance-details {
  overflow: hidden;
  border: 1px solid rgba(67, 72, 87, 0.58);
  border-radius: 6px;
  background: rgba(3, 5, 9, 0.38);
}

.part-maintenance-details summary {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 8px;
  color: #9ba6b8;
  cursor: pointer;
  font-size: 7.5pt;
  font-weight: 900;
}

.part-maintenance-details summary span {
  color: #687386;
  font-weight: 700;
}

.part-maintenance-details > div {
  display: grid;
  gap: 4px;
  max-height: 150px;
  padding: 7px 8px;
  overflow: auto;
  border-top: 1px solid rgba(67, 72, 87, 0.48);
}

.part-maintenance-details pre {
  margin: 0;
  color: #9da8ba;
  font-family: monospace;
  font-size: 7.2pt;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
}

.part-maintenance-empty {
  padding: 18px;
  border: 1px dashed rgba(67, 72, 87, 0.62);
  border-radius: 7px;
  color: #7f899b;
  font-size: 8.5pt;
  text-align: center;
}

@media screen and (max-width: 860px) {
  .part-maintenance-grid { grid-template-columns: 1fr; }
}

@media screen and (max-width: 640px) {
  .part-maintenance-head,
  .part-maintenance-action {
    grid-template-columns: 1fr;
  }

  .part-maintenance-head { flex-direction: column; }
  .part-maintenance-snapshot { width: 100%; }
  .part-maintenance-snapshot > span { min-width: 0; }
  .part-maintenance-run-button { width: 100%; }
}
</style>
